'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState, useCallback } from 'react';

import { UiKitPanelBase } from '@/fo/ui/kit/panel/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { eventCareProgram } from '@/fo/ui/product/info/care-programs';
import { Product } from '@/fo/ui/product/tile/mini';

import { Program } from '../components/Program/Program';
import { ProgramType, GroupedProduct } from '../types';

import { styles } from './UiProductSectionCarePrograms.styles';

export interface UiProductSectionCareProgramsProps {
  programs: ProgramType[];
  renderGrouped: (product: GroupedProduct) => React.ReactNode;
  renderProductTileMini: (product: Product, index: number) => React.ReactNode;
}

export function UiProductSectionCarePrograms({
  programs,
  renderGrouped,
  renderProductTileMini,
  ...props
}: UiProductSectionCareProgramsProps) {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useMediaQuery('md', 'max');

  const slots = styles();

  const tabs = programs.map((program) => ({
    content: (
      <Program
        program={program}
        renderGrouped={renderGrouped}
        renderProduct={renderProductTileMini}
      />
    ),
    header: program.title
  }));

  const onProgramIdChange = useCallback(
    (id: number | string) => {
      const idx = programs.findIndex((program) => program.id === id);

      if (idx !== -1) {
        setActiveIndex(idx);

        setTimeout(() => {
          let offset = 160;

          if (isMobile) {
            offset = 80;
          }
          if (ref.current) {
            const top =
              ref.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: top - offset, behavior: 'smooth' });
          }
        }, 0);
      }
    },
    [programs, isMobile]
  );

  useEffect(() => {
    return eventCareProgram.onIdChange((id) => onProgramIdChange(id));
  }, [onProgramIdChange]);

  return (
    <div
      ref={ref}
      className={slots.base()}
      {...props}
    >
      <h2 className={slots.title()}>{t('CarePrograms')}</h2>

      <UiKitPanelBase
        tabsClassName="w-auto"
        items={tabs}
        view="tab"
        activeIndex={activeIndex}
        onTabChange={(index) => {
          setActiveIndex(index);
        }}
      />
    </div>
  );
}
